
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { DownloadIcon, SparklesIcon } from '@heroicons/react/outline';
import { GetServerSideProps } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { type ImgHTMLAttributes, type SyntheticEvent, useEffect, useRef, useState, } from 'react';
import { Image as ImageType } from 'types';
// import Banner from '@/components/atoms/Banner'; // Black Friday is over
import { getImageBySlug, similaritySearchImagesV2 } from '@/data/images';
import { generatePublicImageSeo } from '@/lib/publicImageSeo';
import { CopyBtn } from '@/components/atoms/CopyBtn';
import Button from '@/components/buttons/Button';
import CallToAction from '@/components/CallToAction';
import ImageStyles from '@/components/images/ImageStyles';
import PublicImageReportModal from '@/components/images/PublicImageReportModal';
import StylePreviewTile from '@/components/images/StylePreviewTile';
import UserImages from '@/components/images/UserImages';
import NewFooter from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import ButtonLink from '@/components/links/ButtonLink';
import MediaSpecs from '@/components/media/MediaSpecs';
import Seo from '@/components/Seo';
import { getOptimizedImageUrl } from '@/utils/useOptimizedImage';
interface ImageProps {
    image: ImageType;
    images: ImageType[];
    isStaticPage?: boolean;
}
const isSpriteSheetPrompt = (prompt: string) => {
    const lower = prompt.toLowerCase();
    return lower.includes('sprite') && lower.includes('sheet');
};
const fallBackToOriginalImage = (event: SyntheticEvent<HTMLImageElement>, originalUrl: string) => {
    const element = event.currentTarget;
    // A resized derivative can be missing or corrupt even when the original is
    // still healthy. Retry only once so a missing original cannot cause a loop.
    if (element.src !== originalUrl) {
        element.src = originalUrl;
    }
};
const ImageWithFallback = ({ alt = '', fallbackSrc, onError, src, ...props }: ImgHTMLAttributes<HTMLImageElement> & {
    fallbackSrc: string;
}) => {
    const imageRef = useRef<HTMLImageElement>(null);
    useEffect(() => {
        const element = imageRef.current;
        // Image errors can happen before React hydrates and attaches onError. The
        // post-hydration check covers that race without giving up resized assets.
        if (element?.complete &&
            element.naturalWidth === 0 &&
            element.src !== fallbackSrc) {
            element.src = fallbackSrc;
        }
    }, [fallbackSrc, src]);
    return (<img {...props} ref={imageRef} src={src} alt={alt} onError={(event) => {
            fallBackToOriginalImage(event, fallbackSrc);
            onError?.(event);
        }}/>);
};
/**
 * Gallery pages that also have an animated version of the image, keyed by
 * slug. These are the handful of outputs that pull enough search traffic to
 * be worth animating individually — the heart diagram alone lands ~5k sessions
 * a month at 82% bounce, and a beating heart is something the static diagram
 * those visitors searched for cannot give them.
 *
 * Deliberately a curated map rather than a column: the underlying image row
 * belongs to the user who generated it and must not be rewritten to point at
 * someone else's clip.
 */
const ANIMATED_OUTPUTS: Record<string, string> = {
    'human-heart-anatomy-detailed-diagram-physiological-explanation': 'https://media.easy-peasy.ai/landing/image-styles/heart-anatomy-hero.mp4',
};
const ImagePage = ({ image, images, isStaticPage = true }: ImageProps) => {
    const router = useRouter();
    const [showSticky, setShowSticky] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportedImageTakenDown, setReportedImageTakenDown] = useState(false);
    const isSpriteSheet = isSpriteSheetPrompt(image?.prompt || '');
    // Diagram/education searches ("heart diagram", "respiratory system diagram")
    // are a large share of gallery traffic — speak to that intent instead of the
    // generic art pitch.
    const isDiagram = /diagram|anatomy|labeled|cross.?section|life cycle|flow.?chart/i.test(`${image?.title || ''} ${image?.prompt || ''}`);
    const animatedUrl = ANIMATED_OUTPUTS[image?.slug || ''];
    useEffect(() => {
        const handleScroll = () => {
            // Show sticky section after scrolling 2 viewport heights
            const scrollThreshold = window.innerHeight * 1.3;
            setShowSticky(window.scrollY > scrollThreshold);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);
    if (!image) {
        return null;
    }
    return (<main className='relative bg-gray-100 dark:bg-slate-900'>
      {/* <Banner /> Black Friday is over */}
      <Seo templateTitle={image.title + ' | AI Art Generator'} description={image.description + ' Generated by AI.'} 
    // Social crawlers cannot run the client-side fallback used below, so
    // advertise the authoritative original instead of a derived asset.
    image={image.image_url}/>
      <LandingPageNavigation skipUserContext/>
      <section>
        <div className='relative isolate'>
          <svg className='absolute inset-x-0 top-0 -z-10 h-[64rem] w-full stroke-gray-200 [mask-image:radial-gradient(32rem_32rem_at_center,white,transparent)] dark:stroke-gray-800' aria-hidden='true'>
            <defs>
              <pattern id='1f932ae7-37de-4c0a-a8b0-a6e3b4d44b84' width={200} height={200} x='50%' y={-1} patternUnits='userSpaceOnUse'>
                <path d='M.5 200V.5H200' fill='none'/>
              </pattern>
            </defs>
            <svg x='50%' y={-1} className='overflow-visible fill-gray-50 dark:hidden'>
              <path d='M-200 0h201v201h-201Z M600 0h201v201h-201Z M-400 600h201v201h-201Z M200 800h201v201h-201Z' strokeWidth={0}/>
            </svg>
            <rect width='100%' height='100%' strokeWidth={0} fill='url(#1f932ae7-37de-4c0a-a8b0-a6e3b4d44b84)'/>
          </svg>
          <div className='absolute left-1/2 right-0 top-0 -z-10 -ml-24 transform-gpu overflow-hidden blur-3xl lg:ml-24 xl:ml-48' aria-hidden='true'>
            <div className='aspect-[801/1036] w-[50.0625rem] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30' style={{
            clipPath: 'polygon(63.1% 29.5%, 100% 17.1%, 76.6% 3%, 48.4% 0%, 44.6% 4.7%, 54.5% 25.3%, 59.8% 49%, 55.2% 57.8%, 44.4% 57.2%, 27.8% 47.9%, 35.1% 81.5%, 0% 97.7%, 39.2% 100%, 35.2% 81.4%, 97.2% 52.8%, 63.1% 29.5%)',
        }}/>
          </div>
          <div className='overflow-hidden'>
            <div className='mx-auto max-w-7xl px-6 pb-6 pt-8 sm:pt-12 lg:px-8 lg:pt-16 xl:px-3'>
              <div className='mx-auto max-w-2xl gap-x-14 lg:mx-0 lg:flex lg:max-w-none lg:items-center'>
                <div className='flex justify-end gap-8 sm:justify-start lg:mt-0 lg:pl-0 xl:w-1/2 xl:shrink-0'>
                  <script type='application/ld+json'>
                    {`
                        {
                            "@context": "https://schema.org/",
                            "@type": "ImageObject",
                            "contentUrl": "${image.image_url}",
                            "license": "https://creativecommons.org/licenses/by-nd/4.0/deed.en",
                            "creditText": "Easy-Peasy.AI - AI Image Generator",
                            "creator": {
                                "@type": "Company",
                                "name": "Easy-Peasy.AI"
                            },
                            "copyrightNotice": "Easy-Peasy.AI - AI Image Generator"
                        }
                    `}
                  </script>
                  {/* Wrapper so an animated version stacks under the still
            instead of becoming a second child of this flex row. */}
                  <div className='w-full'>
                    <picture className='w-full'>
                      <ImageWithFallback src={getOptimizedImageUrl(image.image_url, 'medium')} fallbackSrc={image.image_url} alt={image.title} title={image.title} className='w-full rounded-md' width={700} height={700}/>
                    </picture>
                    {animatedUrl && (<figure className='mt-6'>
                        <StylePreviewTile src={animatedUrl} alt={`${image.title} — animated`} videoMaxHeightClassName='max-h-[26rem]'/>
                        <figcaption className='mt-3 text-sm text-gray-600 dark:text-slate-400'>
                          The same image brought to life with AI —{' '}
                          <Link href='/ai-animate-image' className='font-medium text-primary-600 underline hover:text-primary-500 dark:text-primary-400'>
                            animate your own image
                          </Link>
                          .
                        </figcaption>
                      </figure>)}
                  </div>
                </div>
                <div className='mt-10 w-full max-w-xl lg:mt-0  xl:max-w-2xl'>
                  {/* <div className='mb-10 flex items-center text-xl font-semibold text-indigo-600'>
          <SparklesIcon className='h-5 w-5 text-primary-600' />{' '}
          Generate stunning images and art with AI
        </div> */}
                  {image.used_credits === 8 && (<span className='inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium leading-4 text-green-800'>
                      HD
                    </span>)}
                  <h1 className='text-2xl mb-5 font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-3xl'>
                    {image.h1}
                  </h1>
                  <div className='w-full sm:w-auto'>
                    <ButtonLink href={isSpriteSheet ? '/sprite-generator' : '/ai-images'} variant='dark' onClick={() => {
            if (!isSpriteSheet) {
                localStorage.setItem('savedImagePrompt', image.prompt);
            }
        }}>
                      {isSpriteSheet
            ? 'Create sprite sheets like this \u2192'
            : isDiagram
                ? 'Create your own custom diagram \u2192'
                : 'Create images like this \u2192'}
                    </ButtonLink>
                  </div>
                  <div className='mt-5 flex items-center '>
                    <h2 className='text-lg font-bold tracking-tight text-gray-700 dark:text-slate-400 sm:text-xl'>
                      Prompt
                    </h2>
                    <CopyBtn value={image.prompt} variant='plain' showText={false}/>
                  </div>
                  <div className='relative mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800'>
                    <p className='text-base leading-relaxed text-gray-700 dark:text-slate-200 whitespace-pre-wrap break-words'>
                      {image.prompt}
                    </p>
                  </div>
                  <div className='mt-6 space-y-3'>
                    {!image.prompt
            .toLowerCase()
            .includes(image.user_prompt.toLowerCase().trim()) && (<div className='text-sm text-gray-600'>
                        <span className='font-semibold text-gray-800'>
                          Original Prompt:
                        </span>{' '}
                        {image.user_prompt}
                      </div>)}
                    <div className='text-sm text-gray-600'>
                      <span className='font-semibold text-gray-800'>
                        Model:
                      </span>{' '}
                      {image.model || 'AI'}
                    </div>
                    <div className='text-sm text-gray-600'>
                      <span className='font-semibold text-gray-800'>
                        Created on
                      </span>{' '}
                      {image?.created_at &&
            new Intl.DateTimeFormat('en-US').format(new Date(image?.created_at))}{' '}
                      <button type='button' onClick={() => setShowReportModal(true)} className='ml-3 text-sm underline hover:no-underline hover:text-red-600 transition-colors'>
                        Report
                      </button>
                    </div>
                    {image?.updated_at &&
            image?.created_at &&
            new Date(image.updated_at).toDateString() !==
                new Date(image.created_at).toDateString() && (<div className='text-sm text-gray-600'>
                          <span className='font-semibold text-gray-800'>
                            Updated on
                          </span>{' '}
                          {new Intl.DateTimeFormat('en-US').format(new Date(image.updated_at))}{' '}
                        </div>)}
                  </div>

                  <MediaSpecs url={image.image_url} request={image.request} usedCredits={image.used_credits}/>

                  <div className='mt-6'>
                    <span className='text-xs text-gray-500'>
                      <a href='https://creativecommons.org/licenses/by/4.0/deed.en' className='text-xs text-gray-500 underline hover:no-underline' target='_blank' rel='nofollow noreferrer noopener'>
                        License
                      </a>
                      : Free to use with a backlink to Easy-Peasy.AI
                    </span>
                  </div>

                  <div className='mt-10 flex flex-wrap '>
                    <div className='w-full space-x-3 sm:w-auto'>
                      <CopyBtn value={`https://easy-peasy.ai/ai-image-generator/images/${image.slug}`} text='Copy URL'/>

                      <Button variant='light' onClick={() => window.open(`${image.image_url}`)}>
                        <DownloadIcon className='mr-1 h-4 w-4 '/>
                        Download
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <UserImages initialImages={images} currentImage={String(image.slug)}/>

      {/* Sticky CTA Section */}
      <div className={`fixed bottom-0 left-0 right-0 z-50 transform transition-transform duration-300 ease-in-out ${showSticky ? 'translate-y-0' : 'translate-y-full'}`}>
        <div className='border-t border-gray-200 bg-white/95 shadow-lg backdrop-blur-sm dark:border-slate-700 dark:bg-slate-800/95'>
          <div className='mx-auto max-w-4xl px-4 py-3 sm:px-6 lg:px-8'>
            <div className='flex flex-col items-center gap-4 sm:flex-row sm:justify-between'>
              <div className='hidden sm:block'>
                <ImageWithFallback src={getOptimizedImageUrl(image.image_url, 'medium')} fallbackSrc={image.image_url} alt='AI Art Creation' width={88} height={88} className='rounded-lg object-cover'/>
              </div>
              <div className='flex-1 text-center sm:mx-6 sm:text-left'>
                <h2 className='text-lg font-semibold text-gray-900 dark:text-slate-200 sm:text-xl'>
                  {isSpriteSheet
            ? 'Create Your Own Sprite Sheets'
            : isDiagram
                ? 'Create Custom Diagrams with AI'
                : 'Create Your Own AI Images'}
                </h2>
                <p className='mt-1 text-sm text-gray-600 dark:text-slate-400'>
                  {isSpriteSheet
            ? 'Generate game-ready sprite sheets with our AI sprite generator'
            : isDiagram
                ? 'Generate a diagram for any topic — perfect for teaching, homework and presentations'
                : 'Generate stunning AI images with our easy-to-use image generator'}
                </p>
              </div>
              <div className='w-full flex-shrink-0 sm:w-auto'>
                <Button onClick={() => {
            if (!isSpriteSheet) {
                localStorage.setItem('savedImagePrompt', image.prompt);
            }
            window.open(isSpriteSheet ? '/sprite-generator' : '/ai-images', '_self');
        }} variant='dark' className='w-full justify-center sm:w-auto'>
                  <SparklesIcon className='mr-2 h-5 w-5'/>
                  Start Creating Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ImageStyles showImages={false}/>
      <CallToAction preset='ai-images'/>
      <NewFooter />
      <PublicImageReportModal imageSlug={image.slug || ''} showModal={showReportModal} onTakenDown={() => setReportedImageTakenDown(true)} onClose={() => {
            setShowReportModal(false);
            if (reportedImageTakenDown) {
                void router.replace('/ai-images');
            }
        }}/>
    </main>);
};
export default ImagePage;
// export async function getStaticPaths() {
//   const images = await getPublicImagesPaginated(1, 10);
//   const paths = images.map((image) => ({
//     params: { uuid: image.slug.toString() },
//   }));
//   return { paths, fallback: true };
// }
const getServerSideProps: GetServerSideProps = async (ctx) => {
    let image = await getImageBySlug(ctx?.params?.uuid as string);
    const locale = ctx.locale || 'en';
    //redirect 301
    if (image && locale !== 'en') {
        return {
            redirect: {
                destination: `/ai-image-generator/images/${image.slug}`,
                permanent: true,
            },
        };
    }
    if (!image || !image.is_public || image.is_video) {
        return {
            notFound: true,
        };
    }
    // Self-heal rows whose SEO generation was lost (pre-2026-08-23 the toggle
    // route ran it after res.json() without waitUntil). Done synchronously so
    // this very response — often a crawler's — already carries real meta tags.
    if (!image.title || !image.description) {
        const healed = await generatePublicImageSeo(image, image.user_id as string);
        if (healed) {
            image = (await getImageBySlug(image.slug as string)) || image;
        }
    }
    const images = await similaritySearchImagesV2(Number(image.id), image.embedding as string, 1, 24);
    return {
        props: {
            image: {
                created_at: image.created_at,
                updated_at: image.updated_at,
                id: image.id,
                image_url: image.image_url,
                //@ts-ignore
                prompt: 
                //@ts-ignore
                (image.response_data && image.response_data[0]?.revised_prompt) ||
                    image.prompt,
                user_prompt: image.user_prompt,
                used_credits: image.used_credits,
                request: image.request || null,
                title: image.title_new || image.title,
                description: image.description,
                h1: image.h1,
                slug: image.slug,
                model: image.model,
            },
            images,
        },
    };
};

    async function __Next_Translate__getServerSideProps__1a13a29a23b__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-image-generator/images/[uuid]',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1a13a29a23b__ as getServerSideProps }
  